import { Color, ContentType } from "@brookbush/database";
import { type Highlight, UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { Popover, PopoverPanel } from "@headlessui/react";
import clsx from "clsx";
import { AnimatePresence } from "framer-motion";
import { MessageSquarePlusIcon, XIcon } from "lucide-react";
import { type RefObject, useEffect, useMemo, useState } from "react";

import AnnotationsNote from "./AnnotationsNote";
import type { AnnotationsProps } from "./AnnotationsProvider";
import { useAccess, useUserAction } from "~/hooks";
import useHighlights from "~/hooks/useHighlights";
import useTrackCTA from "~/hooks/useTrackCTA";
import { getRangeOffsetsInContainer } from "~/utils/helpers";

interface Props {
	range: Range | null;
	setRange: (val: Range | null) => void;
	options: AnnotationsProps;
	parentRef: RefObject<HTMLDivElement | null>;
	activeHighlight: Highlight | null;
	setActiveHighlight: (val: Highlight | null) => void;
}

const AnnotationsProviderTooltip = ({
	range,
	options,
	activeHighlight,
	setActiveHighlight,
	setRange,
	parentRef,
}: Props) => {
	const { setUserAction } = useUserAction();
	const { isSignedIn } = useAuth();
	const { isMember } = useAccess();
	const trackCTA = useTrackCTA();

	const [isAddingNote, setIsAddingNote] = useState(false);

	const {
		addHighlight,
		removeHighlight,
		updateHighlight,
		addNoteToHighlight,
		deleteNoteFromHighlight,
	} = useHighlights(options);

	const bounds = useMemo(() => range?.getBoundingClientRect(), [range]);

	useEffect(() => {
		setIsAddingNote(false);
	}, [activeHighlight, range]);

	return (
		<Popover>
			<AnimatePresence>
				{range && (
					<PopoverPanel
						static
						style={{
							top:
								bounds?.top! -
								parentRef.current?.getBoundingClientRect().top! +
								bounds?.height!,
							left:
								bounds!.left - parentRef.current?.getBoundingClientRect().left!,
						}}
						className={clsx(
							"absolute z-10 flex min-w-max max-w-max select-none items-stretch gap-2 rounded-full rounded-tl-none border bg-white p-1 shadow-lg dark:bg-slate-700",

							{
								"mt-4": options.contentType === ContentType.COURSE,
								"mt-5": options.contentType === ContentType.GLOSSARY,
								"mt-6": options.contentType === ContentType.ARTICLE,
							},
						)}
					>
						{Object.values(Color).map((color) => (
							<button
								key={color}
								onClick={async () => {
									if (!isSignedIn) {
										trackCTA(UserAction.SIGN_UP, "Annotation Highlight");
										setUserAction(UserAction.SIGN_UP);
									} else if (!isMember) {
										trackCTA(
											UserAction.BECOME_A_MEMBER,
											"Annotation Highlight",
										);
										setUserAction(UserAction.BECOME_A_MEMBER);
									} else {
										if (activeHighlight && color === activeHighlight.color) {
											await removeHighlight(activeHighlight.id);

											setActiveHighlight(null);
											setRange(null);
										} else if (activeHighlight) {
											await updateHighlight(activeHighlight.id, {
												color,
											});

											setActiveHighlight(null);
											setRange(null);
										} else {
											const { startOffset, endOffset } =
												getRangeOffsetsInContainer(parentRef.current!, range)!;

											await addHighlight({
												color,
												text: range.toString(),
												startOffset,
												endOffset,
											});

											setRange(null);
										}
									}
								}}
								className={clsx(
									"my-auto flex h-6 w-6 items-center justify-center rounded-full border first:ml-2 hover:brightness-95",
									{
										"border-red-300 bg-red-200 dark:border-red-300/30 dark:bg-red-300/30":
											color === Color.RED,
										"border-amber-300 bg-amber-200 dark:border-amber-300/30 dark:bg-amber-300/30":
											color === Color.YELLOW,
										"border-green-300 bg-green-200 dark:border-green-300/30 dark:bg-green-300/30":
											color === Color.GREEN,
									},
								)}
							>
								{activeHighlight?.color === color && (
									<XIcon
										size={18}
										className={clsx({
											"text-red-700 dark:text-red-200": color === Color.RED,
											"text-amber-700 dark:text-amber-200":
												color === Color.YELLOW,
											"text-green-700 dark:text-green-200":
												color === Color.GREEN,
										})}
									/>
								)}
							</button>
						))}

						<div className="h-auto border-r" />
						{activeHighlight && (
							<button
								onClick={() => setIsAddingNote(true)}
								className="flex items-center gap-1.5 rounded-full phablet:px-2 px-2.5 phablet:py-1 py-0.5 font-medium hover:bg-slate-100 dark:hover:bg-slate-600"
							>
								<MessageSquarePlusIcon className="size-5" />
							</button>
						)}

						{(isAddingNote || activeHighlight?.note) && activeHighlight && (
							<AnnotationsNote
								highlight={activeHighlight}
								onChange={async (text) => {
									if (text === "" || !text || text.trim() === "") {
										if (activeHighlight.note?.id) {
											await deleteNoteFromHighlight(
												activeHighlight.id,
												activeHighlight.note.id,
											);
										}
									} else {
										await addNoteToHighlight(activeHighlight.id, text);
									}
								}}
							/>
						)}
					</PopoverPanel>
				)}
			</AnimatePresence>
		</Popover>
	);
};

export default AnnotationsProviderTooltip;
